import { useEffect, useState, startTransition, useMemo } from 'react';
import Grid from '@mui/material/Grid2';
import { Box } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import { ChartComponent } from 'src/view/components/chartComponent';
import { CreditData } from 'src/interface';
import { CreditCard } from 'src/view/components/cardComponent';
import { useAppState } from 'src/state-management/state-provider';
import { useSnackbar } from 'src/view/components/SnackbarService';
import FullPageMessage from 'src/view/components/FullPageMessage';
import FullPageLoader from 'src/view/components/FullPageLoader';
import clientService from 'src/clientService';
import { useParams } from 'react-router-dom';
import SubscriptionLock from 'src/view/components/SubscriptionLock';
import { Home } from '@mui/icons-material';
import { ProjectStatusEnum, UserRole } from 'src/api/Api';
import { SampleProjectId } from 'src/enum';
HighchartsMore(Highcharts);

const CarbonCredit = () => {
  const [chartData, setChartData] = useState<{ name: string; value1: number; value2: number }[]>([]);
  const [chartDataBuffer, setChartDataBuffer] = useState<{ name: string; value1: number; value2: number }[]>([]);
  const { id } = useParams<{ id: string; siteId: string }>(); // Get ID from route params
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state, dispatch } = useAppState();
  const { selectedProject, user } = state;
  const { projectDetails } = selectedProject;
  const { has_carbon_module } =
    projectDetails || {};
  const { carbonAnalysis } = state.selectedProject;
  const [draftView, setDraftView] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterCredit, setFilterCredit] = useState(carbonAnalysis?.credits);

  useEffect(() => {
    const isDRAFT = projectDetails?.project_status === ProjectStatusEnum.Draft;
    const isUnderDevelopment = projectDetails?.project_status === ProjectStatusEnum.UnderDevelopment;

    if (isDRAFT) {
      setDraftView(true);
      return;
    }

    if (isUnderDevelopment && has_carbon_module && id) {
      // fetch data 
      setLoading(true);
      setError(null);
      clientService.api
        .readCarbonProjectSummaryApiV1ProjectsProjectIdCarbonSummaryGet(id)
        .then((response) => {
          startTransition(() => {
            dispatch({
              type: 'SET_PROJECT_CARBON_ANALYSIS',
              payload: response.data,
            });
          });
          setFilterCredit(response.data.credits);
          setLoading(false);
          setError(null);
        })
        .catch((apiError) => {
          console.error('Failed to fetch carbon credit details:', apiError);
          const errorMessage =
            apiError.response?.data?.detail ||
            `Failed to load carbon credit details for ID: ${id}.`;
          setError(errorMessage);
          showSnackbar({
            message: 'Error fetching carbon credit details.',
            type: snackbarType.ERROR,
          });
          setLoading(false);
        });
    }
  }, [id, dispatch, has_carbon_module, projectDetails?.project_status, showSnackbar, snackbarType.ERROR]);
  useEffect(() => {
    if (filterCredit) {
      setInitialData(filterCredit as CreditData);
    }
  }, [filterCredit]);

      const isUserRole = useMemo(() => {
      if (!user?.role || user.role.length === 0) return false;
      // Show only if user has ONLY User role (no Admin or Employee)
      const roles = user.role;
      const hasUserRole = roles.includes(UserRole.User);
      const hasAdminRole = roles.includes(UserRole.Admin);
      const hasEmployeeRole = roles.includes(UserRole.Employee);
      // Only show if user has User role and doesn't have Admin or Employee
      return hasUserRole && !hasAdminRole && !hasEmployeeRole;
    }, [user]);
    const isSampleProject = projectDetails?.project_id === SampleProjectId;
    // Disable exporting when project is the sample project AND the current user
    // has only the "User" role. Otherwise allow exporting.
    const allowExporting = !(isSampleProject && isUserRole);
    // Ensure Highcharts global exporting option follows our allowExporting flag
    useEffect(() => {
      try {
        Highcharts.setOptions({
          exporting: {
            enabled: allowExporting,
          },
        });
      } catch (e) {
        // Ignore if Highcharts isn't ready; this is defensive
        console.warn('Failed to set Highcharts exporting option', e);
      }
    }, [allowExporting]);

  if (draftView) {
    return (
      <SubscriptionLock
        message="Register your project with us to unlock advanced features and insights."
        ctaLabel="Register Project"
        onCtaClick={() => window.open('https://calendly.com/jaya-kandir-darukaa/new-meeting', '_blank')}
        previewChartType='bar'
      />
    );
  }

  if (loading) {
    return (
      <FullPageLoader
        title="Just a moment, crafting your project view..."
        message="Almost there..."
      />
    );
  }
  if (error) {
    return (
      <FullPageMessage
        headline="Uh-oh! The project data took an unexpected detour."
        subHeadline={`We're having a little trouble loading the details. The system reports: ${error}`}
        ctaText="Go to Home"
        onCtaClick={() => {
          window.location.href = '/';
        }}
        ctaIcon={<Home />}
      />
    );
  }

  // Handle Reload
  const setInitialData = (filterCredit: CreditData) => {
    const bufferUnits = (filterCredit && filterCredit.buffer_units) || {};
    const estimatedIssued = (filterCredit && filterCredit.estimated_issued_vcu) || {};
    const estimatedRetired = (filterCredit && filterCredit.estimated_retired_vcu) || {};
    const chartData_local = Object.keys(estimatedIssued)
      .filter((key) => key !== 'unit')
      .map((year) => ({
        name: year,
        value1: estimatedIssued[year] || 0,
        value2: estimatedRetired[year] || 0,
      }));
    setChartData(chartData_local);
    const chartDataBuffer_local = Object.keys(bufferUnits)
      .filter((key) => key !== 'unit')
      .map((year) => ({
        name: year,
        value1: bufferUnits[year] || 0,
        value2: estimatedIssued[year] || 0,
      }));
    setChartDataBuffer(chartDataBuffer_local);
  };

  const CreditCardData = [
    {
      label: 'Total Estimated VCUs',
      value: filterCredit?.total_estimated_issued_vcu,
      xs: 4,
      sm: 2.6,
    },
    {
      label: 'Average Price Per VCU',
      value: filterCredit?.average_price_per_vcu,
      xs: 4,
      sm: 2.6,
    },
    {
      label: 'Total VCUs Retired',
      value: filterCredit?.total_vcu_retired,
      xs: 4,
      sm: 2.6,
    },
    {
      label: 'VCUs available for Sale',
      value: filterCredit?.vcu_available_for_sale,
      xs: 4,
      sm: 2.6,
    },
  ];

  return (
    <>
      {filterCredit && filterCredit?.total_estimated_issued_vcu ? (
        <Box sx={{ mt: 5 }}>
          <Box
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid #ccc',
              padding: '10px',
              backgroundColor: '#fff',
              margin: '15px 1px',
              borderRadius: '8px',
              boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important',
            }}
          >
            <CreditCard data={CreditCardData} />
          </Box>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <ChartComponent
                chartType="line"
                chartTitle="Estimated VCUs Per Year"
                data={Object.keys(filterCredit.vcu || {}).map((key) => ({
                  name: key,
                  value1: Number(filterCredit.vcu[key as keyof typeof filterCredit.vcu]) || 0,
                }))}
                title="VCUs"
                seriesValue1="VCUs"
                yTitle="Year"
                export={allowExporting}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ChartComponent
                chartType={'bar'}
                chartTitle={'Estimated VCUs / Buffer Unit'}
                data={chartDataBuffer}
                title="VCUs"
                seriesValue1="Buffer Unit"
                seriesValue2="Estimated VCUs"
                yTitle="Year"
                export={allowExporting}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ChartComponent
                chartType={'bar'}
                chartTitle={'Estimated VCUs /Retired'}
                data={chartData}
                title="VCUs"
                seriesValue1="Estimated VCUs"
                seriesValue2="Estimated Retired VCUs"
                yTitle="Year"
                export={allowExporting}
              />
            </Grid>
          </Grid>
        </Box>
      ) : <></>}
    </>
  );
};

export default CarbonCredit;
